import { useEffect, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, useMap, LayersControl } from "react-leaflet";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";

// Dynamic import of leaflet-draw to avoid Vite issues
if (typeof window !== "undefined") {
  import("leaflet-draw");
}

// ── Color palette for map layers ──
export const MAP_COLORS = {
  // Parcelas de referencia (Plot de finca) — amarillo dorado
  reference: { stroke: "#d4a017", fill: "#faad14", weight: 3, fillOpacity: 0.18 },
  // Parcela auditada / nueva geometría — verde
  auditPlot: { stroke: "#389e0d", fill: "#52c41a", weight: 4, fillOpacity: 0.35 },
  // Parcelas históricas (PlotReal) — púrpura
  historical: { stroke: "#531dab", fill: "#722ed1", weight: 3, fillOpacity: 0.22 },
  // Ruta realizada — azul cian
  route: { stroke: "#0891b2", fill: "#06b6d4", weight: 4, fillOpacity: 0 },
};

// ── Custom pane names and z-index for guaranteed layer ordering ──
const PANE_REFERENCE = "referencePane";   // z-index 401 (bottom)
const PANE_HISTORICAL = "historicalPane"; // z-index 402
const PANE_ROUTE = "routePane";          // z-index 403
const PANE_AUDIT = "auditPane";          // z-index 404 (top)

/**
 * Creates custom Leaflet panes with explicit z-index to guarantee layer stacking order.
 * Must be rendered inside MapContainer before any layer components.
 */
const CustomPanes = () => {
  const map = useMap();

  useEffect(() => {
    // overlayPane default z-index is 400; we go above it
    if (!map.getPane(PANE_REFERENCE)) {
      const p = map.createPane(PANE_REFERENCE);
      p.style.zIndex = "401";
      p.style.pointerEvents = "none";
    }
    if (!map.getPane(PANE_HISTORICAL)) {
      const p = map.createPane(PANE_HISTORICAL);
      p.style.zIndex = "402";
      p.style.pointerEvents = "none";
    }
    if (!map.getPane(PANE_ROUTE)) {
      const p = map.createPane(PANE_ROUTE);
      p.style.zIndex = "403";
      p.style.pointerEvents = "none";
    }
    if (!map.getPane(PANE_AUDIT)) {
      const p = map.createPane(PANE_AUDIT);
      p.style.zIndex = "404";
    }
  }, [map]);

  return null;
};

interface PlotMapEditorProps {
  geometry: GeoJSON.Geometry | null;
  plotName: string;
  onGeometryChange?: (geometry: GeoJSON.Geometry | null) => void;
  editable?: boolean;
  referencePlots?: GeoJSON.FeatureCollection | null;
  historicalPlots?: GeoJSON.FeatureCollection | null;
  routeData?: GeoJSON.FeatureCollection | null;
  /** Source of the geometry: 'current_plot', 'similar_plot', or 'none' */
  geometrySource?: string;
}

/**
 * Component to add drawing controls to the map
 */
const DrawControls = ({
  onGeometryChange,
  geometry,
  layerRef,
  onLayerReady,
  geometrySource,
}: {
  onGeometryChange?: (geometry: GeoJSON.Geometry | null) => void;
  geometry: GeoJSON.Geometry | null;
  layerRef?: React.MutableRefObject<L.FeatureGroup | null>;
  onLayerReady?: (layer: L.FeatureGroup) => void;
  geometrySource?: string;
}) => {
  const map = useMap();
  const { t } = useTranslation();
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());

  useEffect(() => {
    const drawnItems = drawnItemsRef.current;
    
    // Expose layer to parent component
    if (layerRef) {
      layerRef.current = drawnItems;
    }
    if (onLayerReady) {
      onLayerReady(drawnItems);
    }
    
    map.addLayer(drawnItems);

    // Initialize draw control with explicit polygon configuration
    const drawControl = new L.Control.Draw({
      position: "topleft",
      edit: {
        featureGroup: drawnItems,
      },
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: false, // Disabled to avoid leaflet-draw bug with readableArea
          metric: true,
          shapeOptions: {
            color: MAP_COLORS.auditPlot.stroke,
            weight: MAP_COLORS.auditPlot.weight,
            fillOpacity: MAP_COLORS.auditPlot.fillOpacity,
            fillColor: MAP_COLORS.auditPlot.fill,
            pane: PANE_AUDIT,
          },
          repeatMode: false,
          drawError: {
            color: "#e74c3c",
            timeout: 1000,
          },
          icon: new L.DivIcon({
            iconSize: new L.Point(8, 8),
            className: "leaflet-div-icon leaflet-editing-icon",
          }),
          touchIcon: new L.DivIcon({
            iconSize: new L.Point(20, 20),
            className: "leaflet-div-icon leaflet-editing-icon leaflet-touch-icon",
          }),
        },
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
    });

    map.addControl(drawControl);

    // Don't disable map.dragging during edit/delete — Leaflet's
    // L.Marker drag handlers already stop propagation at mousedown,
    // so dragging a vertex won't pan the map. Disabling caused two
    // user-visible bugs: (a) you couldn't pan to reach a vertex on
    // the other side of the parcela, and (b) the markers felt
    // "sticky" because the map tried to handle the drag itself.
    // Drawing a NEW polygon still disables dragging because each
    // click adds a vertex and panning would feel surprising.
    map.on(L.Draw.Event.DRAWSTART, () => {
      map.dragging.disable();
      message.info(t("map.draw_start") || "Haz clic para agregar puntos. Doble clic o Enter para finalizar.", 5);
    });

    // Handle draw vertex event (when a point is added)
    map.on(L.Draw.Event.DRAWVERTEX, () => {
      // no-op — Leaflet handles the visual feedback itself.
    });

    // Handle draw created event
    map.on(L.Draw.Event.CREATED, (event: any) => {
      map.dragging.enable();
      const layer = event.layer;
      
      // Add new layer (don't clear existing ones to allow multiple polygons)
      drawnItems.addLayer(layer);
      
      // Convert all layers to GeoJSON and combine into MultiPolygon if needed
      const allLayers = drawnItems.getLayers();

      if (allLayers.length === 1) {
        // Single polygon
        const geoJson = layer.toGeoJSON();
        const newGeometry = geoJson.geometry;

        if (onGeometryChange) {
          onGeometryChange(newGeometry);
        }
        
        message.success(t("map.polygon_created") || "Polígono creado correctamente");
      } else {
        // Multiple polygons - create MultiPolygon
        const coordinates = allLayers.map((l: any) => {
          const geoJson = l.toGeoJSON();
          return geoJson.geometry.coordinates;
        });
        
        const multiPolygon: GeoJSON.Geometry = {
          type: "MultiPolygon",
          coordinates: coordinates,
        };

        if (onGeometryChange) {
          onGeometryChange(multiPolygon);
        }
        
        message.success(`${allLayers.length} ${t("map.multipolygon_created") || "polígonos combinados en MultiPolygon"}`);
      }
    });

    // Handle draw edited event
    map.on(L.Draw.Event.EDITED, () => {
      // Get all layers after editing
      const allLayers = drawnItems.getLayers();

      if (allLayers.length === 1) {
        // Single polygon
        const geoJson = (allLayers[0] as any).toGeoJSON();
        const newGeometry = geoJson.geometry;
        
        if (onGeometryChange) {
          onGeometryChange(newGeometry);
        }
        
        message.success(t("map.polygon_edited") || "Polígono editado correctamente");
      } else if (allLayers.length > 1) {
        // Multiple polygons - create MultiPolygon
        const coordinates = allLayers.map((l: any) => {
          const geoJson = l.toGeoJSON();
          return geoJson.geometry.coordinates;
        });
        
        const multiPolygon: GeoJSON.Geometry = {
          type: "MultiPolygon",
          coordinates: coordinates,
        };
        
        if (onGeometryChange) {
          onGeometryChange(multiPolygon);
        }
        
        message.success(t("map.multipolygon_edited") || "MultiPolygon editado correctamente");
      }
    });

    // Handle draw deleted event
    map.on(L.Draw.Event.DELETED, () => {
      if (onGeometryChange) {
        onGeometryChange(null);
      }
      
      message.success(t("map.polygon_deleted") || "Polígono eliminado");
    });

    // Cleanup
    return () => {
      map.dragging.enable();
      map.removeControl(drawControl);
      map.removeLayer(drawnItems);
      map.off(L.Draw.Event.DRAWSTART);
      map.off(L.Draw.Event.DRAWVERTEX);
      map.off(L.Draw.Event.CREATED);
      map.off(L.Draw.Event.EDITED);
      map.off(L.Draw.Event.DELETED);
    };
  }, [map, onGeometryChange]);

  return (
    <>
      <GeometryDisplay geometry={geometry} drawnItems={drawnItemsRef.current} geometrySource={geometrySource} />
    </>
  );
};

/**
 * Component to display reference plots from the farm
 */
const ReferencePlotsLayer = ({ 
  referencePlots,
  layerRef,
  onLayerReady,
}: { 
  referencePlots: GeoJSON.FeatureCollection | null;
  layerRef?: React.MutableRefObject<L.FeatureGroup | null>;
  onLayerReady?: (layer: L.FeatureGroup) => void;
}) => {
  const map = useMap();
  const layerGroupRef = useRef<L.FeatureGroup>(new L.FeatureGroup());

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    
    // Expose layer to parent component
    if (layerRef) {
      layerRef.current = layerGroup;
    }
    if (onLayerReady) {
      onLayerReady(layerGroup);
    }
    
    map.addLayer(layerGroup);

    return () => {
      map.removeLayer(layerGroup);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    layerGroup.clearLayers();

    if (!referencePlots || !referencePlots.features) return;

    referencePlots.features.forEach((feature) => {
      if (!feature.geometry) return;

      const geometry = feature.geometry;
      let layer: L.Layer | null = null;

      if (geometry.type === 'Polygon') {
        const coords = (geometry as any).coordinates[0];
        layer = L.polygon(
          coords.map((coord: number[]) => [coord[1], coord[0]]),
          {
            color: MAP_COLORS.reference.stroke,
            weight: MAP_COLORS.reference.weight,
            fillOpacity: MAP_COLORS.reference.fillOpacity,
            fillColor: MAP_COLORS.reference.fill,
            pane: PANE_REFERENCE,
            interactive: false,
          }
        );
      } else if (geometry.type === 'MultiPolygon') {
        const coords = (geometry as any).coordinates;
        const polygons = coords.map((polygonCoords: any) =>
          polygonCoords[0].map((coord: number[]) => [coord[1], coord[0]])
        );
        layer = L.polygon(polygons, {
          color: MAP_COLORS.reference.stroke,
          weight: MAP_COLORS.reference.weight,
          fillOpacity: MAP_COLORS.reference.fillOpacity,
          fillColor: MAP_COLORS.reference.fill,
          pane: PANE_REFERENCE,
          interactive: false,
        });
      }

      if (layer) {
        layerGroup.addLayer(layer);
      }
    });

    // Fit map bounds to reference plots after adding them
    if (layerGroup.getLayers().length > 0) {
      const bounds = layerGroup.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [20, 20], maxZoom: 19 });
      }
    }
  }, [referencePlots, map]);

  return null;
};

/**
 * Component to display historical plots (PlotReal) from previous seasons
 */
const HistoricalPlotsLayer = ({
  historicalPlots,
  layerRef,
  onLayerReady,
}: {
  historicalPlots: GeoJSON.FeatureCollection | null;
  layerRef?: React.MutableRefObject<L.FeatureGroup | null>;
  onLayerReady?: (layer: L.FeatureGroup) => void;
}) => {
  const map = useMap();
  const layerGroupRef = useRef<L.FeatureGroup>(new L.FeatureGroup());

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    if (layerRef) {
      layerRef.current = layerGroup;
    }
    if (onLayerReady) {
      onLayerReady(layerGroup);
    }
    map.addLayer(layerGroup);
    return () => {
      map.removeLayer(layerGroup);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    layerGroup.clearLayers();

    if (!historicalPlots || !historicalPlots.features) return;

    historicalPlots.features.forEach((feature) => {
      if (!feature.geometry) return;

      const geom = feature.geometry;
      let layer: L.Layer | null = null;

      if (geom.type === "Polygon") {
        const coords = (geom as any).coordinates[0];
        layer = L.polygon(
          coords.map((coord: number[]) => [coord[1], coord[0]]),
          {
            color: MAP_COLORS.historical.stroke,
            weight: MAP_COLORS.historical.weight,
            fillOpacity: MAP_COLORS.historical.fillOpacity,
            fillColor: MAP_COLORS.historical.fill,
            dashArray: "6 4",
            pane: PANE_HISTORICAL,
            interactive: false,
          }
        );
      } else if (geom.type === "MultiPolygon") {
        const coords = (geom as any).coordinates;
        const polygons = coords.map((polygonCoords: any) =>
          polygonCoords[0].map((coord: number[]) => [coord[1], coord[0]])
        );
        layer = L.polygon(polygons, {
          color: MAP_COLORS.historical.stroke,
          weight: MAP_COLORS.historical.weight,
          fillOpacity: MAP_COLORS.historical.fillOpacity,
          fillColor: MAP_COLORS.historical.fill,
          dashArray: "6 4",
          pane: PANE_HISTORICAL,
          interactive: false,
        });
      }

      if (layer) {
        layerGroup.addLayer(layer);
      }
    });
  }, [historicalPlots, map]);

  return null;
};

/**
 * Component to display route performed (LineString) on the audit day
 */
const RouteLayer = ({
  routeData,
  layerRef,
  onLayerReady,
}: {
  routeData: GeoJSON.FeatureCollection | null;
  layerRef?: React.MutableRefObject<L.FeatureGroup | null>;
  onLayerReady?: (layer: L.FeatureGroup) => void;
}) => {
  const map = useMap();
  const layerGroupRef = useRef<L.FeatureGroup>(new L.FeatureGroup());

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    if (layerRef) layerRef.current = layerGroup;
    if (onLayerReady) onLayerReady(layerGroup);
    map.addLayer(layerGroup);
    return () => { map.removeLayer(layerGroup); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    layerGroup.clearLayers();

    if (!routeData || !routeData.features) return;

    routeData.features.forEach((feature) => {
      if (!feature.geometry) return;
      const geom = feature.geometry;

      if (geom.type === "LineString") {
        const coords = (geom as GeoJSON.LineString).coordinates;
        const latLngs = coords.map((c) => [c[1], c[0]] as [number, number]);
        const polyline = L.polyline(latLngs, {
          color: MAP_COLORS.route.stroke,
          weight: MAP_COLORS.route.weight,
          opacity: 0.85,
          pane: PANE_ROUTE,
          interactive: false,
        });

        layerGroup.addLayer(polyline);
      }
    });
  }, [routeData, map]);

  return null;
};

/**
 * Component to display existing geometry and load it into drawnItems for editing.
 * When the geometry comes from a similar_plot, the fill opacity is reduced so
 * underlying reference / historical layers remain visible.
 */
const GeometryDisplay = ({ 
  geometry,
  drawnItems,
  geometrySource,
}: { 
  geometry: GeoJSON.Geometry | null;
  drawnItems: L.FeatureGroup;
  geometrySource?: string;
}) => {
  const map = useMap();

  useEffect(() => {
    if (!geometry) return;

    // Clear existing items
    drawnItems.clearLayers();

    // When geometry is borrowed from a similar plot, use lower opacity + dashed border
    // so the reference and historical layers underneath remain visible.
    const isBorrowed = geometrySource === "similar_plot";
    const style: L.PathOptions = {
      color: MAP_COLORS.auditPlot.stroke,
      weight: isBorrowed ? 3 : MAP_COLORS.auditPlot.weight,
      fillOpacity: isBorrowed ? 0.12 : MAP_COLORS.auditPlot.fillOpacity,
      fillColor: MAP_COLORS.auditPlot.fill,
      pane: PANE_AUDIT,
      ...(isBorrowed ? { dashArray: "8 6" } : {}),
    };

    // Handle MultiPolygon by converting each polygon to a separate layer
    const addedLayers: L.Polygon[] = [];
    if (geometry.type === "MultiPolygon") {
      const coords = (geometry as any).coordinates;
      coords.forEach((polygonCoords: any) => {
        const polygon = L.polygon(
          polygonCoords[0].map((coord: number[]) => [coord[1], coord[0]]),
          style
        );
        drawnItems.addLayer(polygon);
        addedLayers.push(polygon);
      });
    } else if (geometry.type === "Polygon") {
      // Handle single Polygon
      const coords = (geometry as any).coordinates[0];
      const polygon = L.polygon(
        coords.map((coord: number[]) => [coord[1], coord[0]]),
        style
      );
      drawnItems.addLayer(polygon);
      addedLayers.push(polygon);
    }

    // Leaflet.draw's edit-toolbar checks whether `featureGroup` has any
    // layers when its button is rendered. Because the geometry is loaded
    // AFTER the toolbar is built, the Edit button stays disabled and
    // the user can't grab the vertices of the pre-loaded polygon. Re-
    // firing `layeradd` on the FeatureGroup nudges leaflet-draw to
    // refresh its mode-button state and recognise the polygon as
    // editable — same effect a draw-created polygon would have.
    addedLayers.forEach((layer) => {
      drawnItems.fire("layeradd", { layer });
    });

    // Fit bounds to geometry
    const bounds = drawnItems.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20], maxZoom: 19 });
    }

    return () => {
      // Don't remove layers here as they need to stay for editing
    };
  }, [map, geometry, drawnItems, geometrySource]);

  return null;
};

/**
 * Component to add coordinate search control using native Leaflet Control
 */
const CoordinateSearchControl = () => {
  const map = useMap();
  const { t } = useTranslation();

  useEffect(() => {
    // Create custom control
    const CoordControl = L.Control.extend({
      options: {
        position: "topright",
      },

      onAdd: function (map: L.Map) {
        const container = L.DomUtil.create(
          "div",
          "leaflet-bar leaflet-control leaflet-control-custom"
        );
        container.style.backgroundColor = "white";
        container.style.padding = "8px";
        container.style.borderRadius = "4px";
        container.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";

        // Create input
        const input = L.DomUtil.create("input", "", container);
        input.type = "text";
        input.placeholder = t("map.search_input_placeholder") || "Ciudad o Lat, Lng";
        input.style.width = "220px";
        input.style.padding = "4px 8px";
        input.style.border = "1px solid #d9d9d9";
        input.style.borderRadius = "2px";
        input.style.marginRight = "4px";

        // Create button
        const button = L.DomUtil.create("button", "", container);
        button.innerHTML = t("map.search_button") || "🔍 Buscar";
        button.style.padding = "4px 12px";
        button.style.backgroundColor = "#1890ff";
        button.style.color = "white";
        button.style.border = "none";
        button.style.borderRadius = "2px";
        button.style.cursor = "pointer";

        // Handle button click
        const handleCenter = async () => {
          const value = input.value.trim();

          if (!value) {
            message.warning(t("map.search_no_input") || "Por favor ingrese una ubicación o coordenadas");
            return;
          }

          // Check if input is coordinates (lat, lng)
          const parts = value.split(",").map((p) => p.trim());

          if (parts.length === 2) {
            const lat = parseFloat(parts[0]);
            const lng = parseFloat(parts[1]);

            if (!isNaN(lat) && !isNaN(lng)) {
              if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                message.error(t("map.search_invalid_coords") || "Coordenadas fuera de rango válido");
                return;
              }

              map.setView([lat, lng], 16);
              message.success(t("map.search_centered") || "Mapa centrado en las coordenadas");
              return;
            }
          }

          // Otherwise, search by place name using Nominatim
          button.disabled = true;
          button.innerHTML = t("map.search_button_loading") || "⏳ Buscando...";

          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
                value
              )}&countrycodes=es&limit=1`
            );

            if (!response.ok) {
              throw new Error("Error en la búsqueda");
            }

            const data = await response.json();

            if (data.length === 0) {
              message.error(
                t("map.search_not_found") || "No se encontró la ubicación. Intente con otro nombre o use coordenadas."
              );
              return;
            }

            const result = data[0];
            const lat = parseFloat(result.lat);
            const lng = parseFloat(result.lon);

            map.setView([lat, lng], 14);
            message.success(`${t("map.search_location_found") || "Ubicación encontrada"}: ${result.display_name}`);
          } catch (error) {
            message.error(t("map.search_error") || "Error al buscar la ubicación");
            console.error("Geocoding error:", error);
          } finally {
            button.disabled = false;
            button.innerHTML = t("map.search_button") || "🔍 Buscar";
          }
        };

        button.onclick = handleCenter;
        input.onkeypress = (e) => {
          if (e.key === "Enter") {
            handleCenter();
          }
        };

        // Prevent map interactions when using the control
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      },
    });

    const control = new CoordControl();
    map.addControl(control);

    return () => {
      map.removeControl(control);
    };
  }, [map]);

  return null;
};

/**
 * Component to add layer control for toggling reference plots and audit plot
 */
const LayerToggleControl = ({
  referencePlotsLayer,
  auditPlotLayer,
  historicalPlotsLayer,
  routeLayer,
}: {
  referencePlotsLayer: L.FeatureGroup | null;
  auditPlotLayer: L.FeatureGroup | null;
  historicalPlotsLayer?: L.FeatureGroup | null;
  routeLayer?: L.FeatureGroup | null;
}) => {
  const map = useMap();
  const controlRef = useRef<L.Control | null>(null);
  const refCheckboxRef = useRef<HTMLInputElement | null>(null);
  const auditCheckboxRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!referencePlotsLayer && !auditPlotLayer && !historicalPlotsLayer && !routeLayer) return;

    // Create custom control
    const LayerControl = L.Control.extend({
      options: {
        position: "topright",
      },

      onAdd: function (mapInstance: L.Map) {
        const container = L.DomUtil.create(
          "div",
          "leaflet-bar leaflet-control leaflet-control-layers"
        );
        container.style.backgroundColor = "white";
        container.style.padding = "10px";
        container.style.borderRadius = "4px";
        container.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
        container.style.minWidth = "180px";

        // Title
        const title = L.DomUtil.create("div", "", container);
        title.innerHTML = "<strong>Capas</strong>";
        title.style.marginBottom = "8px";
        title.style.fontSize = "14px";

        // --- Order: Referencia (bottom) → Históricas → Auditadas (top) ---

        // 1. Reference plots checkbox
        if (referencePlotsLayer) {
          const refDiv = L.DomUtil.create("div", "", container);
          refDiv.style.marginBottom = "6px";
          refDiv.style.display = "flex";
          refDiv.style.alignItems = "center";

          const refCheckbox = L.DomUtil.create("input", "", refDiv) as HTMLInputElement;
          refCheckbox.type = "checkbox";
          refCheckbox.checked = true;
          refCheckbox.style.marginRight = "8px";
          refCheckbox.style.cursor = "pointer";
          refCheckbox.id = "ref-plots-checkbox";
          refCheckboxRef.current = refCheckbox;

          const refLabel = L.DomUtil.create("label", "", refDiv);
          refLabel.innerHTML = `<span style="display:inline-block;width:12px;height:12px;background:${MAP_COLORS.reference.fill};border:2px solid ${MAP_COLORS.reference.stroke};border-radius:2px;margin-right:6px;vertical-align:middle;"></span>Parcelas de Referencia`;
          refLabel.style.cursor = "pointer";
          refLabel.style.fontSize = "13px";
          refLabel.htmlFor = "ref-plots-checkbox";

          L.DomEvent.on(refCheckbox, 'change', function(e: Event) {
            L.DomEvent.stopPropagation(e);
            const checkbox = e.target as HTMLInputElement;
            
            if (checkbox.checked) {
              if (!mapInstance.hasLayer(referencePlotsLayer)) {
                mapInstance.addLayer(referencePlotsLayer);
              }
            } else {
              if (mapInstance.hasLayer(referencePlotsLayer)) {
                mapInstance.removeLayer(referencePlotsLayer);
              }
            }
          });
        }

        // 2. Historical plots checkbox
        if (historicalPlotsLayer) {
          const histDiv = L.DomUtil.create("div", "", container);
          histDiv.style.marginBottom = "6px";
          histDiv.style.display = "flex";
          histDiv.style.alignItems = "center";

          const histCheckbox = L.DomUtil.create("input", "", histDiv) as HTMLInputElement;
          histCheckbox.type = "checkbox";
          histCheckbox.checked = true;
          histCheckbox.style.marginRight = "8px";
          histCheckbox.style.cursor = "pointer";
          histCheckbox.id = "hist-plots-checkbox";

          const histLabel = L.DomUtil.create("label", "", histDiv);
          histLabel.innerHTML = `<span style="display:inline-block;width:12px;height:12px;background:${MAP_COLORS.historical.fill};border:2px solid ${MAP_COLORS.historical.stroke};border-radius:2px;margin-right:6px;vertical-align:middle;"></span>Parcelas Históricas`;
          histLabel.style.cursor = "pointer";
          histLabel.style.fontSize = "13px";
          histLabel.htmlFor = "hist-plots-checkbox";

          L.DomEvent.on(histCheckbox, 'change', function(e: Event) {
            L.DomEvent.stopPropagation(e);
            const checkbox = e.target as HTMLInputElement;
            if (checkbox.checked) {
              if (!mapInstance.hasLayer(historicalPlotsLayer)) {
                mapInstance.addLayer(historicalPlotsLayer);
              }
            } else {
              if (mapInstance.hasLayer(historicalPlotsLayer)) {
                mapInstance.removeLayer(historicalPlotsLayer);
              }
            }
          });
        }

        // 3. Route checkbox
        if (routeLayer) {
          const routeDiv = L.DomUtil.create("div", "", container);
          routeDiv.style.marginBottom = "6px";
          routeDiv.style.display = "flex";
          routeDiv.style.alignItems = "center";

          const routeCheckbox = L.DomUtil.create("input", "", routeDiv) as HTMLInputElement;
          routeCheckbox.type = "checkbox";
          routeCheckbox.checked = true;
          routeCheckbox.style.marginRight = "8px";
          routeCheckbox.style.cursor = "pointer";
          routeCheckbox.id = "route-checkbox";

          const routeLabel = L.DomUtil.create("label", "", routeDiv);
          routeLabel.innerHTML = `<span style="display:inline-block;width:12px;height:4px;background:${MAP_COLORS.route.stroke};border-radius:2px;margin-right:6px;vertical-align:middle;"></span>Ruta del día`;
          routeLabel.style.cursor = "pointer";
          routeLabel.style.fontSize = "13px";
          routeLabel.htmlFor = "route-checkbox";

          L.DomEvent.on(routeCheckbox, 'change', function(e: Event) {
            L.DomEvent.stopPropagation(e);
            const checkbox = e.target as HTMLInputElement;
            if (checkbox.checked) {
              if (!mapInstance.hasLayer(routeLayer)) mapInstance.addLayer(routeLayer);
            } else {
              if (mapInstance.hasLayer(routeLayer)) mapInstance.removeLayer(routeLayer);
            }
          });
        }

        // 4. Audit plot checkbox (top layer)
        if (auditPlotLayer) {
          const auditDiv = L.DomUtil.create("div", "", container);
          auditDiv.style.display = "flex";
          auditDiv.style.alignItems = "center";

          const auditCheckbox = L.DomUtil.create("input", "", auditDiv) as HTMLInputElement;
          auditCheckbox.type = "checkbox";
          auditCheckbox.checked = true;
          auditCheckbox.style.marginRight = "8px";
          auditCheckbox.style.cursor = "pointer";
          auditCheckbox.id = "audit-plot-checkbox";
          auditCheckboxRef.current = auditCheckbox;

          const auditLabel = L.DomUtil.create("label", "", auditDiv);
          auditLabel.innerHTML = `<span style="display:inline-block;width:12px;height:12px;background:${MAP_COLORS.auditPlot.fill};border:2px solid ${MAP_COLORS.auditPlot.stroke};border-radius:2px;margin-right:6px;vertical-align:middle;"></span>Parcela Auditada`;
          auditLabel.style.cursor = "pointer";
          auditLabel.style.fontSize = "13px";
          auditLabel.htmlFor = "audit-plot-checkbox";

          L.DomEvent.on(auditCheckbox, 'change', function(e: Event) {
            L.DomEvent.stopPropagation(e);
            const checkbox = e.target as HTMLInputElement;
            
            if (checkbox.checked) {
              if (!mapInstance.hasLayer(auditPlotLayer)) {
                mapInstance.addLayer(auditPlotLayer);
              }
            } else {
              if (mapInstance.hasLayer(auditPlotLayer)) {
                mapInstance.removeLayer(auditPlotLayer);
              }
            }
          });
        }

        // Prevent map interactions when using the control
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      },
    });

    const control = new LayerControl();
    controlRef.current = control;
    map.addControl(control);

    return () => {
      if (controlRef.current) {
        map.removeControl(controlRef.current);
        controlRef.current = null;
      }
    };
  }, [map, referencePlotsLayer, auditPlotLayer, historicalPlotsLayer, routeLayer]);

  return null;
};

/**
 * PlotMapEditor Component
 * Map component for viewing and editing plot geometry with satellite imagery
 */
export const PlotMapEditor = ({ 
  geometry, 
  plotName,
  onGeometryChange,
  editable = true,
  referencePlots = null,
  historicalPlots = null,
  routeData = null,
  geometrySource,
}: PlotMapEditorProps) => {
  const [referencePlotsLayer, setReferencePlotsLayer] = useState<L.FeatureGroup | null>(null);
  const [auditPlotLayer, setAuditPlotLayer] = useState<L.FeatureGroup | null>(null);
  const [historicalPlotsLayer, setHistoricalPlotsLayer] = useState<L.FeatureGroup | null>(null);
  const [routeLayer, setRouteLayer] = useState<L.FeatureGroup | null>(null);

  // Callback refs that trigger re-render when layers are ready
  const referencePlotsLayerRef = useRef<L.FeatureGroup | null>(null);
  const auditPlotLayerRef = useRef<L.FeatureGroup | null>(null);
  const historicalPlotsLayerRef = useRef<L.FeatureGroup | null>(null);
  const routeLayerRef = useRef<L.FeatureGroup | null>(null);

  // Sync refs to state so LayerToggleControl re-renders when layers mount
  const onReferencePlotsLayerReady = useCallback((layer: L.FeatureGroup) => {
    referencePlotsLayerRef.current = layer;
    setReferencePlotsLayer(layer);
  }, []);
  const onAuditPlotLayerReady = useCallback((layer: L.FeatureGroup) => {
    auditPlotLayerRef.current = layer;
    setAuditPlotLayer(layer);
  }, []);
  const onHistoricalPlotsLayerReady = useCallback((layer: L.FeatureGroup) => {
    historicalPlotsLayerRef.current = layer;
    setHistoricalPlotsLayer(layer);
  }, []);
  const onRouteLayerReady = useCallback((layer: L.FeatureGroup) => {
    routeLayerRef.current = layer;
    setRouteLayer(layer);
  }, []);

  // Calculate center from geometry
  const getCenter = (): [number, number] => {
    if (!geometry) {
      return [40.0, -4.0]; // Default center (Spain)
    }

    const coords: any = (geometry as any).coordinates;
    
    if (!coords) {
      return [40.0, -4.0];
    }

    let coordArray: any = coords;
    
    if (geometry.type === "Polygon" && Array.isArray(coords) && coords.length > 0) {
      coordArray = coords[0];
    } else if (geometry.type === "MultiPolygon" && Array.isArray(coords) && coords.length > 0) {
      coordArray = coords[0][0];
    }

    if (Array.isArray(coordArray) && coordArray.length > 0) {
      let sumLat = 0;
      let sumLng = 0;
      let count = 0;

      coordArray.forEach((coord: any) => {
        if (Array.isArray(coord) && coord.length >= 2) {
          sumLng += coord[0];
          sumLat += coord[1];
          count++;
        }
      });

      if (count > 0) {
        return [sumLat / count, sumLng / count];
      }
    }

    return [40.0, -4.0];
  };

  return (
    <div
      className="plot-map-editor"
      style={{ position: "relative", height: "100%", width: "100%" }}
    >
      <style>{`
        /* Render the top-right controls on a single horizontal line —
           search input first, layer toggle right after. */
        .plot-map-editor .leaflet-top.leaflet-right {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          gap: 6px;
        }
        .plot-map-editor .leaflet-top.leaflet-right > .leaflet-control {
          margin: 10px 0 0 0;
          float: none;
          clear: none;
        }
        .plot-map-editor .leaflet-top.leaflet-right > .leaflet-control:last-child {
          margin-right: 10px;
        }
      `}</style>
      <MapContainer
        center={getCenter()}
        zoom={geometry ? 15 : 6}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
      >
        {/* Base layers */}
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Satélite (ArcGIS)">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="&copy; Esri"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satélite (Google)">
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
              attribution="&copy; Google"
              maxZoom={20}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="OpenStreetMap">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="IGN Base">
            <TileLayer
              url="https://tms-ign-base.idee.es/1.0.0/IGNBaseTodo/{z}/{x}/{-y}.jpeg"
              attribution="&copy; IGN"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {/* Custom panes for guaranteed z-order */}
        <CustomPanes />

        {/* Reference plots layer (farm plots for visual reference) */}
        <ReferencePlotsLayer referencePlots={referencePlots} layerRef={referencePlotsLayerRef} onLayerReady={onReferencePlotsLayerReady} />

        {/* Historical plots layer (PlotReal from previous seasons) */}
        <HistoricalPlotsLayer historicalPlots={historicalPlots} layerRef={historicalPlotsLayerRef} onLayerReady={onHistoricalPlotsLayerReady} />

        {/* Route layer (route performed on audit day) */}
        <RouteLayer routeData={routeData} layerRef={routeLayerRef} onLayerReady={onRouteLayerReady} />

        {/* Drawing controls (includes geometry display) */}
        {editable && <DrawControls onGeometryChange={onGeometryChange} geometry={geometry} layerRef={auditPlotLayerRef} onLayerReady={onAuditPlotLayerReady} geometrySource={geometrySource} />}

        {/* Coordinate search control — sits to the left of the
            base-layer picker on the same row (see CSS below). The
            old "parcelas de referencia / históricas / ruta" toggle
            panel was removed at the user's request. */}
        <CoordinateSearchControl />
      </MapContainer>
    </div>
  );
};
